import React from "react";

/**
 * The single Workspace operational row (WORKSPACE-FEATURE-001 S8-S9,
 * S5.1 layers 1-2; authoritative original at commit 23d500e27;
 * reference `docs/design/prototypes/workspace-v1-reference.png`).
 *
 * Ambient KPIs on the left, campaign context and the campaign controls
 * on the right, in ONE row inside ONE glass surface. Product identity,
 * notifications and account controls belong to the global application
 * shell and are not repeated here.
 *
 * AW-VIS-2 MADE THIS FIXTURE-FREE AND SHARED RATHER THAN DUPLICATING IT
 * ---------------------------------------------------------------------
 * This component has always owned the authoritative row geometry, and
 * Production has never rendered it -- not in any of the 22 commits of
 * `AgentWorkspaceProductionShell`'s history. Production grew its own
 * campaign section instead, and when PR-A promoted that section from
 * AW-2A's 380px side rail to a full-width header it carried the rail's
 * `w-full flex-col` control stack with it. In a 380px column that
 * stacks two compact buttons; across a full-width header it produces
 * the full-width stacked Start Dialing / Logout Campaign that failed
 * visual acceptance.
 *
 * The blocker to reuse was this file's own coupling: `KPI_ITEMS` from
 * `workspaceFixtures`, and a `viewModel` that pulls `fakeRuntime`. So
 * the fix is the one PR-A-VIS-1 applied to `WorkspaceMainCard` -- remove
 * the coupling instead of cloning the component. Everything is now
 * injected, this file imports nothing but React, and there is one
 * operational row instead of two.
 *
 * The development shell supplies its fixture KPI values and its
 * fixture status/quarantine controls (see
 * `WorkspaceFixtureStatusControls`); Production supplies honest
 * unavailable KPI slots and its real campaign controls. Neither can
 * leak into the other.
 *
 * WHY `kpis` CARRIES VALUES AS STRINGS AND NEVER FORMATS THEM
 * -----------------------------------------------------------
 * S7.8 requires that an unavailable count never render as `0`, and this
 * component cannot tell the difference between a measured zero and an
 * absent contract. So it renders exactly what it is handed. Deciding
 * what a slot says when its value is unknown belongs to whoever owns
 * the contract, not to the row.
 *
 * Pure presentation: no state, no fetch, no knowledge of the dialer
 * lifecycle.
 */

/**
 * One KPI: value above, label beneath. Typography is the authoritative
 * original's -- `text-lg font-semibold leading-none` over an 11px
 * label -- and is what makes the row read as ambient awareness rather
 * than as a dashboard (S9.1, S9.4).
 */
export function KpiCluster({ testKey, label, value }) {
  return (
    <div className="flex flex-col gap-0.5" data-testid={`workspace-kpi-${testKey}`}>
      <span className="text-lg font-semibold leading-none text-slate-100">{value}</span>
      <span className="text-[11px] leading-tight text-slate-500">{label}</span>
    </div>
  );
}

/**
 * Campaign identity: an uppercase eyebrow over the campaign name.
 *
 * `nameTestId` exists so a caller that already has an established
 * acceptance selector for the campaign name keeps it. Production's is
 * `workspace-logged-in-campaign-name` and predates this component;
 * renaming it would cost real assertions to prove nothing.
 */
export function CampaignContext({ campaignName, nameTestId, children }) {
  return (
    <div className="flex cursor-default flex-col gap-0.5" data-testid="workspace-campaign-identity">
      <span className="text-[11px] uppercase tracking-wide text-slate-500">Campaign</span>
      <span className="text-sm font-medium text-slate-200" data-testid={nameTestId}>
        {campaignName || "—"}
      </span>
      {children}
    </div>
  );
}

export default function WorkspaceOperationalRow({
  kpis = [],
  kpiNote = null,
  campaign = null,
  controls = null,
  notice = null,
}) {
  return (
    <div
      className="glass flex shrink-0 flex-wrap items-center justify-between gap-4 rounded-xl px-4 py-3"
      data-testid="workspace-operational-row"
    >
      <section
        className="flex flex-wrap items-center gap-x-7 gap-y-2"
        role="group"
        aria-label="Workspace KPIs"
        data-testid="workspace-kpi-row"
      >
        <div className="flex flex-wrap items-center gap-x-7 gap-y-2" data-testid="workspace-kpi-region">
          {kpis.map((item) => (
            <KpiCluster key={item.key} testKey={item.key} label={item.label} value={item.value} />
          ))}
        </div>
        {kpiNote ? (
          <p className="text-[11px] text-slate-500" data-testid="workspace-kpi-note">
            {kpiNote}
          </p>
        ) : null}
      </section>

      {/*
        The right operational group. `justify-between` above puts it at
        the far edge of the same row as the KPIs -- the authoritative
        arrangement, and what S8.3 requires: campaign identity, status
        and the primary action read as one conceptual group rather than
        being scanned across opposite edges.

        `flex-wrap items-center` and NOT `w-full flex-col`. The controls
        are intrinsically sized and inline. This is the single property
        whose loss produced the full-width stacked controls.
      */}
      <div className="flex flex-wrap items-center justify-end gap-4">
        {campaign}
        {controls}
      </div>

      {notice}
    </div>
  );
}
